import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { catalogPreview } from '@/config/localPreview'
import { useOfferStore } from './offer'

export const useCartStore = defineStore('cart', () => {
  const offer = useOfferStore()
  // State
  const items = ref([])
  const sessionId = ref(null)
  const loading = ref(false)
  const error = ref(null)
  
  // Getters
  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })
  
  const subtotal = computed(() => {
    return items.value.reduce((total, item) => total + (item.priceAtTime * item.quantity), 0)
  })
  
  const hasItems = computed(() => items.value.length > 0)
  const pricedItems = computed(() => items.value.map(item => ({ ...item, priceAtTime: offer.price(item.priceAtTime) })))
  const pricedSubtotal = computed(() => Math.round(pricedItems.value.reduce((sum, item) => sum + item.priceAtTime * item.quantity, 0) * 100) / 100)
  const discount = computed(() => Math.round((subtotal.value - pricedSubtotal.value) * 100) / 100)
  
  /* -------------------------------------------------------------------------
   * Failures a shopper should never have to think about.
   *
   * "We could not add that to your cart. Please try again." was shown for
   * every failure, including the one that costs the most: a deploy restarting
   * the API for thirty seconds. The shopper is told to do the work themselves,
   * given no reason, and most of them leave instead.
   *
   * So: retry once, quietly, when the failure is one a retry actually fixes —
   * the request never reached a server, or the server said it was momentarily
   * unavailable. Only what survives that reaches the shopper, and it reaches
   * them in the server's own words (PROTECTION / CLAUDE.md: no dead ends).
   * ---------------------------------------------------------------------- */
  const TRANSIENT_STATUSES = [502, 503, 504, 429]
  const isTransient = (err) => !err.response || TRANSIENT_STATUSES.includes(err.response.status)

  async function retrying(operation, { attempts = 2, delayMs = 900 } = {}) {
    let lastError
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await operation()
      } catch (err) {
        lastError = err
        if (attempt === attempts || !isTransient(err)) throw err
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    }
    throw lastError
  }

  /** What to tell the shopper. The server's words first; ours only as a fallback. */
  function describe(err, fallback) {
    // Ours first for the failures that are ours: a shopper reading
    // "Service Unavailable" learns nothing and is told nothing about their cart.
    if (!err.response) return 'We could not reach the shop just then. Your cart is safe — this usually clears in a moment.'
    if (TRANSIENT_STATUSES.includes(err.response.status)) {
      return 'The shop is busy right now. Nothing was lost — give it a moment and try again.'
    }
    // The server's own words otherwise: it knows why it refused, unless what it
    // said is machine noise that would mean nothing to a shopper.
    const served = err.response.data?.error
    if (served && !/^(database|internal|unexpected)/i.test(served)) return served
    return fallback
  }
  
  // Helper to get or create session
  async function ensureSession() {
    if (catalogPreview) return null
    if (!sessionId.value) {
      // Check localStorage first
      const savedSessionId = localStorage.getItem('cartSessionId')
      if (savedSessionId) {
        sessionId.value = savedSessionId
        // Try to load the cart
        await loadCart()
      } else {
        // Create new cart session
        await createCart()
      }
    }
    return sessionId.value
  }
  
  // Actions
  async function createCart() {
    try {
      loading.value = true
      error.value = null
      const response = await api.shop.cart.create()
      sessionId.value = response.data.data.sessionId
      localStorage.setItem('cartSessionId', sessionId.value)
      items.value = []
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to create cart'
      console.error('Cart creation error:', err)
    } finally {
      loading.value = false
    }
  }
  
  async function loadCart(showLoading = true) {
    if (!sessionId.value) return
    
    try {
      if (showLoading) {
        loading.value = true
      }
      error.value = null
      const response = await api.shop.cart.get(sessionId.value)
      const cartData = response.data.data
      
      // Map backend items to frontend format
      items.value = cartData.items.map(item => ({
        id: item.id,
        productId: item.product.id,
        name: item.product.name,
        price: item.product.price,
        priceAtTime: item.priceAtTime,
        quantity: item.quantity,
        imageUrl: item.product.imageUrl,
        sku: item.product.sku,
        lineTotal: item.lineTotal,
        quantityStep: item.product.quantityStep || 1,
        caseQuantity: item.product.caseQuantity,
        purchaseLabel: item.product.purchaseLabel,
        currentPrice: item.product.price
      }))
    } catch (err) {
      if (err.response?.status === 404) {
        // Cart not found or expired, create new one
        localStorage.removeItem('cartSessionId')
        sessionId.value = null
        items.value = []
      } else {
        error.value = err.response?.data?.error || 'Failed to load cart'
        console.error('Cart load error:', err)
      }
    } finally {
      if (showLoading) {
        loading.value = false
      }
    }
  }
  
  async function addItem(product, quantity = 1) {
    try {
      await ensureSession()
      loading.value = true
      error.value = null

      const add = () => api.shop.cart.addItem(sessionId.value, product.id, quantity * (product.quantityStep || 1))

      let response
      try {
        response = await retrying(add)
      } catch (err) {
        // The cart this browser remembers is gone — expired, or pruned. That is
        // not the shopper's problem to solve by clicking again: start a new one
        // and add the bottle they asked for.
        if (err.response?.status === 404) {
          await createCart()
          response = await retrying(add)
        } else {
          throw err
        }
      }

      // Reload cart to get updated state
      await loadCart()

      return response.data.data
    } catch (err) {
      error.value = describe(err, 'We could not add that to your cart.')
      console.error('Add to cart error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function removeItem(productId) {
    try {
      await ensureSession()
      loading.value = true
      error.value = null
      
      await api.shop.cart.removeItem(sessionId.value, productId)
      
      // Reload cart to get updated state
      await loadCart()
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to remove item'
      console.error('Remove item error:', err)
    } finally {
      loading.value = false
    }
  }
  
  async function updateQuantity(productId, quantity) {
    try {
      await ensureSession()
      // Don't show loading for quick quantity updates
      error.value = null
      
      await api.shop.cart.updateItem(sessionId.value, productId, quantity)
      
      // Reload cart to get updated state without showing loading spinner
      await loadCart(false)
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to update quantity'
      console.error('Update quantity error:', err)
    }
  }
  
  async function clearCart() {
    try {
      if (!sessionId.value) return
      
      loading.value = true
      error.value = null
      
      await api.shop.cart.clear(sessionId.value)
      items.value = []
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to clear cart'
      console.error('Clear cart error:', err)
    } finally {
      loading.value = false
    }
  }
  
  // A paid order consumes its cart. Forget it here — memory and localStorage
  // together — so the nav badge and the cart page stop showing the bottle that
  // was just bought; the next addItem() starts a fresh session.
  function forgetSession() {
    offer.clear()
    items.value = []
    sessionId.value = null
    error.value = null
    localStorage.removeItem('cartSessionId')
  }
  
  // Initialize cart on store creation. `ready` settles once that first load has
  // either populated `items` or found nothing — pages that decide something on
  // the cart's contents at mount (checkout bouncing an empty cart) await it,
  // otherwise a direct load of /checkout judges the cart before it exists.
  const ready = ensureSession().catch(() => {})
  
  return {
    // State
    items,
    sessionId,
    loading,
    error,
    ready,
    
    // Getters
    itemCount,
    subtotal,
    hasItems,
    pricedItems,
    pricedSubtotal,
    discount,
    
    // Actions
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    loadCart,
    ensureSession,
    forgetSession
  }
})
